import { setPreferredLabel } from '../../../helpers/status-labels/status-labels';
import type { TaskTypeDisplaySource } from '../../../helpers/task-type-labels/task-type-labels.type';

const UNKNOWN_TASK_TYPE_VALUE = 'unknown';

/**
 * Derives the stable work-item-type key a task filters and groups under: the
 * lowercased `raw_type` (the customer's source-system type), so case-variant
 * rows collapse into a single key for this page's client-side filtering.
 * Falls back to the lowercased canonical `type` for rows that predate
 * raw_type population, and to "unknown" when neither field carries a value,
 * so no task ever drops out of type filtering entirely. Keys are NOT valid
 * values for server-side type params — some backend filters match raw_type
 * case-sensitively.
 *
 * @param {TaskTypeDisplaySource} task - Any task-like object exposing `type`, `raw_type`, and `raw_type_label`
 * @returns {string} The lowercased filter/group key, never empty
 */
const getRiskAnalysisTaskTypeValue = (task: TaskTypeDisplaySource): string => {
  const rawTypeValue = task.raw_type?.trim().toLowerCase();
  if (rawTypeValue) {
    return rawTypeValue;
  }
  const canonicalTypeValue = task.type?.trim().toLowerCase();
  return canonicalTypeValue || UNKNOWN_TASK_TYPE_VALUE;
};

/**
 * Builds a lookup from each distinct work-item-type key present in the tasks
 * to the display label both the Types filter and the type risk charts show.
 *
 * Responsibilities:
 * 1. Group tasks by `getRiskAnalysisTaskTypeValue`, seeding each key's label
 *    with the key itself so label-less types render as the raw value rather
 *    than a cosmetically title-cased guess
 * 2. Upgrade each key to its verbatim `raw_type_label` when any grouped task
 *    carries one alongside a raw_type (the label describes the raw_type
 *    specifically), or to the raw_type's own stored casing when no label has
 *    been synced, resolving conflicting casings via `setPreferredLabel` so
 *    the result does not depend on task order
 * 3. Disambiguate distinct keys that end up sharing an identical display
 *    label by suffixing the key in parentheses, keeping every option and
 *    legend entry visually unique
 *
 * @param {TaskTypeDisplaySource[]} tasks - Tasks to derive type labels from
 * @returns {ReadonlyMap<string, string>} Lookup from type key to display label
 */
const buildRiskAnalysisTaskTypeLabels = (tasks: TaskTypeDisplaySource[]): ReadonlyMap<string, string> => {
  const labelByValue = new Map<string, string>();
  tasks.forEach((task) => {
    const value = getRiskAnalysisTaskTypeValue(task);
    const rawType = task.raw_type?.trim();
    if (!labelByValue.has(value)) {
      labelByValue.set(value, value);
    }
    const verbatimLabel = task.raw_type_label?.trim();
    if (rawType) {
      // Without a synced label the raw_type's stored casing is still better
      // display text than the lowercased key (and matches what the side panel
      // renders via resolveTaskTypeLabel).
      setPreferredLabel(labelByValue, value, verbatimLabel || rawType);
    }
  });

  const valueCountByLabel = new Map<string, number>();
  labelByValue.forEach((label) => {
    valueCountByLabel.set(label, (valueCountByLabel.get(label) ?? 0) + 1);
  });

  const disambiguatedLabelByValue = new Map<string, string>();
  labelByValue.forEach((label, value) => {
    // A fallback label IS its own key, so suffixing it would just repeat the
    // value; only verbatim labels colliding across keys need the suffix.
    const isColliding = (valueCountByLabel.get(label) ?? 0) > 1 && label !== value;
    disambiguatedLabelByValue.set(value, isColliding ? `${label} (${value})` : label);
  });
  return disambiguatedLabelByValue;
};

/**
 * Builds the Types filter options for a set of tasks: one option per distinct
 * work-item-type key, valued by the stable lowercased key (what the filter
 * predicate matches on) and labeled with the verbatim source-system casing
 * (falling back to the key when no label has been synced), sorted by label.
 *
 * @param {TaskTypeDisplaySource[]} tasks - Tasks to derive filter options from
 * @returns {{ value: string; label: string }[]} Options sorted by display label
 */
const buildRiskAnalysisTaskTypeOptions = (tasks: TaskTypeDisplaySource[]): { value: string; label: string }[] => {
  return Array.from(buildRiskAnalysisTaskTypeLabels(tasks).entries())
    .map(([value, label]) => ({ value: value, label: label }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export { buildRiskAnalysisTaskTypeLabels, buildRiskAnalysisTaskTypeOptions, getRiskAnalysisTaskTypeValue };
